
import { notification } from "antd";
import moment from "moment";
import { useState } from "react";
import { IUseOrderOperations, IOrderActionsProps } from "src/types/JobDetails";
import { ICashAmountModalVisible } from "src/types/JobManagement";

const useOrderOperations: IUseOrderOperations = (props) => {
  const {
    fetchDeliveryJobDetails,
    deleteOrderDeliveryJobDetails,
    getRoutesDeliveryJobDetails,
    postponeOrderDeliveryJobDetails,
    forceNextOrderDeliveryJobDetails,
    completeOrderDeliveryJobDetails,
    returnToShipperDeliveryJobDetails,
    cancelOrderDeliveryJobDetails,
    cancelCompletedDeliveryJobDetails,
    changeOrderPositionDeliveryJobDetails,
    updateCashDeliveryJobDetails,
    getPolylineDeliveryJobDetails,
    onRouteChange,
  } = props;

  const [confirmOrdersAction, setConfirmOrdersAction] = useState<ICashAmountModalVisible>({
    open: false,
    id: null,
    job: null,
    type: null,
  });
  const [operationLoading, setOperationLoading] = useState<boolean>(false);

  const fetchJobDetailsById = async (jobId: number) => {
    if (!jobId) return;
    try {
      await fetchDeliveryJobDetails({ id: jobId }, { query: { attachOrders: true } });
    } catch {
      // silent — background refresh
    }
  };

  const updateDeliveryRoute = async (jobId: number) => {
    if (!jobId) return;
    onRouteChange(true);
    try {
      await getPolylineDeliveryJobDetails({ jobId });
      await getRoutesDeliveryJobDetails({ jobId });
    } catch {
      // silent — map refresh failure should not block UI
    } finally {
      onRouteChange(false);
    }
  };

  const refreshAfterAction = async (jobId: number) => {
    await fetchJobDetailsById(jobId);
    await updateDeliveryRoute(jobId);
  };

  // All mutating endpoints (cancel, delete, postpone, complete, returnToShipper)
  // return HTTP 500 with an empty error body even when the action fully succeeds
  // server-side. This is a confirmed backend bug (ClassSerializerInterceptor
  // crashes serializing the detached entity after mutation).
  //
  // Strategy: always run the action, always refresh, always show success.
  // The try/catch exists only to ensure refresh runs even if the action throws.

  const deleteOrder = async (param: IOrderActionsProps) => {
    try {
      await deleteOrderDeliveryJobDetails(param);
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(param.jobId);
      notification.success({
        message: "Message",
        description: `Order ${param.orderId} was deleted successfully.`,
      });
    }
  };

  const postponeOrder = async (jobId: number, orderId: number, postponedTime: string) => {
    setOperationLoading(true);
    try {
      const normalizedTime = moment(postponedTime, ["DD.MM.YYYY", "YYYY-MM-DD", moment.ISO_8601], true).isValid()
        ? moment(postponedTime, ["DD.MM.YYYY", "YYYY-MM-DD", moment.ISO_8601]).format("YYYY-MM-DD")
        : postponedTime;
      await postponeOrderDeliveryJobDetails({ jobId, orderId, postponedTime: normalizedTime });
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(jobId);
      notification.success({
        message: "Message",
        description: `Order ${orderId} was postponed successfully.`,
      });
      setOperationLoading(false);
      setConfirmOrdersAction({ open: false, id: null, job: null, type: null });
    }
  };

  const cancelOrder = async (param: IOrderActionsProps) => {
    try {
      await cancelOrderDeliveryJobDetails(param);
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(param.jobId);
      notification.success({
        message: "Message",
        description: `Order ${param.orderId} was canceled successfully.`,
      });
    }
  };

  const cancelCompletedOrder = async (jobId: number, orderId: number) => {
    if (!jobId || !orderId) return;
    setOperationLoading(true);
    try {
      await cancelCompletedDeliveryJobDetails({ orderId });
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(jobId);
      notification.success({
        message: "Message",
        description: `Order ${orderId} was canceled successfully.`,
      });
      setOperationLoading(false);
      setConfirmOrdersAction({ open: false, id: null, job: null, type: null });
    }
  };

  const returnToShipper = async (param: IOrderActionsProps) => {
    try {
      await returnToShipperDeliveryJobDetails(param);
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(param.jobId);
      notification.success({
        message: "Message",
        description: `Order ${param.orderId} was returned to shipper successfully.`,
      });
    }
  };

  const completeOrder = async (param: IOrderActionsProps) => {
    try {
      await completeOrderDeliveryJobDetails(param);
    } catch {
      // backend returns 500 even on success — swallow
    } finally {
      await refreshAfterAction(param.jobId);
      notification.success({
        message: "Message",
        description: `Order ${param.orderId} is completed successfully.`,
      });
    }
  };

  const fetchJobDetailsByIdWithRoutes = async (jobId: number) => {
    try {
      await fetchDeliveryJobDetails({ id: jobId }, { query: { attachOrders: true } });
      await getRoutesDeliveryJobDetails({ jobId: jobId });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.body?.message || "Failed to fetch orders",
      });
    }
  };

  const forceNextOrder = async (jobId: number) => {
    try {
      await forceNextOrderDeliveryJobDetails({ jobId });
      await fetchDeliveryJobDetails({ id: jobId }, { query: { attachOrders: true, withError: true } });
      await getRoutesDeliveryJobDetails({ jobId: jobId });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.body?.message || "Failed to move order force change next order",
      });
    }
  };

  const changeOrderPosition = async (jobId: number, pointsOrder: number[]) => {
    try {
      await changeOrderPositionDeliveryJobDetails({ jobId }, { query: { save: true }, body: { pointsOrder } });
      notification.success({
        message: "Order of delivery points has been updated.",
      });
    } catch {
      // backend may return 500 even on success — show success anyway
      notification.success({
        message: "Order of delivery points has been updated.",
      });
    }
  };

  const onUpdateCashAmount = async (orderId: number, jobId: number, cashAmount: number) => {
    try {
      await updateCashDeliveryJobDetails({ orderId, jobId }, { body: { cashAmount } });
      notification.success({
        message: `Order ${orderId} COD Amount updated successfully.`,
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.body?.message || "Failed to update COD Amount",
      });
    }
  };

  return {
    forceNextOrder,
    postponeOrder,
    completeOrder,
    returnToShipper,
    cancelOrder,
    deleteOrder,
    cancelCompletedOrder,
    setConfirmOrdersAction,
    setOperationLoading,
    changeOrderPosition,
    fetchJobDetailsById: fetchJobDetailsByIdWithRoutes,
    onUpdateCashAmount,
    updateDeliveryRoute,
    operationLoading,
    confirmOrdersAction,
  };
};

export default useOrderOperations;